import { useState, useEffect } from "react";
import { FormikProps } from "formik";
import { CustomSelect } from "../../components";
import {
  Option,
  UserModel,
  MediaModel,
  PresetListModel,
  ProductModel,
  AdminPreset,
} from "@/database";
import { MediaDetailConfig } from "@/utils/utilFunctions/Mapper/MediaDetailConfig";
import { MediaEditableConfig } from "@/utils/utilFunctions/Mapper/EditableConfig";
import { getCreditList } from "@/store/media/media.action";
import { getListUser } from "@/store/user/user.action";
import { setUserList } from "@/store/user/user.slice";
import {
  setImageCreditList,
  setVideoCreditList,
  setAudioCreditList,
} from "@/store/media/media.slice";
import { store } from "../../store";
import { COUNTRIES, STATUS_LIST } from "@/utils/constants/uiConstants";
import { deduplicateContributorOptions } from "@/utils/mediautils/CommonFunctions";
import { hasPolicyValueGreaterThanOne } from "@/utils/mediautils/CommonFunctions";
import { formatEntityName } from "@/utils/utilFunctions/entityName";

import {
  shouldShowField,
  isFieldMandatoryStrict,
} from "@/utils/mediautils/policyHelpers";

interface ContributorsAndCreditorsProps {
  formik: FormikProps<any>;
  currentMediaDetailsForEdit: MediaModel;
  mediaDetailConfig: ReturnType<typeof MediaDetailConfig>;
  mediaEditableConfig: ReturnType<typeof MediaEditableConfig>;
  contributorsList?: Option[];
  constUserList?: any;
  currentPresetDetails?: {
    isLoading: boolean;
    error: string;
    data: PresetListModel;
  };
  adminCurrentPresetDetails?: {
    isLoading: boolean;
    error: string;
    data: AdminPreset;
  };
  isApplied?: boolean;
  producers: any;
  editors: any;
  selectedProduct: ProductModel | null;
  anchors: any;
  scriptWriters: any;
  cameramans: any;
  audioCreditList: Option[];
  videoCreditList: Option[];
  imageCreditList: Option[];
  contributorConfig: any;
  borrowedContributorCredit?: { label: string; value: string }[];
  borrowedCreditData?: { label: string; value: string }[];
  getListAgencies?: (params: any) => void;
  selectedAgency_credits: UserModel[];
  autoSelectAgencyCredit: UserModel[];
  setAutoSelectAgencyCredit: (value: UserModel[]) => void;
  isContentType2?: boolean;
}

/* eslint-disable react/prop-types */
const ContributorsAndCreditors: React.FC<ContributorsAndCreditorsProps> = ({
  contributorConfig,
  formik,
  currentMediaDetailsForEdit,
  mediaDetailConfig,
  mediaEditableConfig,
  contributorsList,
  constUserList,
  currentPresetDetails,
  adminCurrentPresetDetails,
  isApplied,
  producers,
  editors,
  anchors,
  scriptWriters,
  cameramans,
  audioCreditList,
  videoCreditList,
  imageCreditList,
  selectedProduct,
  borrowedContributorCredit,
  borrowedCreditData,
  getListAgencies,
  selectedAgency_credits,
  autoSelectAgencyCredit,
  setAutoSelectAgencyCredit,
  isContentType2 = false,
}) => {
  const [mainListCredit, setMainListCredit] = useState<any>([]);
  const [selecteduserContributorData, setSelecteduserContributorData] =
    useState<UserModel[]>([]);

  useEffect(() => {
    if (borrowedContributorCredit?.length) {
      setSelecteduserContributorData(
        (prev) =>
          deduplicateContributorOptions([
            ...(prev || []),
            ...(borrowedContributorCredit as unknown as UserModel[]),
          ]) as unknown as UserModel[]
      );
    }
  }, [borrowedContributorCredit]);

  useEffect(() => {
    if (borrowedCreditData?.length) {
      setMainListCredit(
        (prev: any[]) =>
          deduplicateContributorOptions([
            ...(prev || []),
            ...(borrowedCreditData as unknown as UserModel[]),
          ]) as UserModel[]
      );
    }
  }, [borrowedCreditData]);

  const getBasePermission = (permission: string): string => {
    return permission;
  };

  const clearUserList = () =>
    store.dispatch(
      setUserList({ data: { list: [], count: 0 }, isLoading: false })
    );

  const handlePermissionChange = (values: string[], key: string) => {
    const newProducers = values.map((value) => value);
    formik.setFieldValue(key, newProducers);
  };

  function processPermissionForSelect(array: string[]) {
    const newarray = array?.map((str) => {
      return getBasePermission(str);
    });
    return newarray;
  }

  useEffect(() => {
    // Only process if currentPresetDetails has data
    if (!isApplied) return;
    if (!adminCurrentPresetDetails?.data) return;

    const mainList =
      adminCurrentPresetDetails?.data?.creditsData?.map((d: any) => {
        return {
          label: d.name + (d?.email ? "(" + d.email + ")" : ""),
          value: d.id,
        };
      }) || [];

    const newMainList =
      constUserList?.map((d: UserModel) => {
        return {
          label: d.name + (d?.email ? "(" + d.email + ")" : ""),
          value: d.id,
        };
      }) || [];

    setMainListCredit(
      (prev) =>
        deduplicateContributorOptions([
          ...(prev || []),
          ...(mainList && mainList?.length > 0 ? mainList : newMainList),
        ]) as UserModel[]
    );

    const updatedUserContributorDataList =
      adminCurrentPresetDetails?.data?.userContributorData?.map((val: any) => {
        return {
          label: ` ${val?.name}  ${val?.email ? "(" + val?.email + ")" : ""} `,
          value: val?.id,
        };
      }) || [];

    setSelecteduserContributorData(
      (prev) =>
        deduplicateContributorOptions([
          ...(prev || []),
          ...(updatedUserContributorDataList as unknown as UserModel[]),
        ]) as UserModel[]
    );
  }, [
    adminCurrentPresetDetails,
    constUserList,
    currentMediaDetailsForEdit,
    isApplied,
  ]);

  useEffect(() => {
    if (isApplied) return;

    const mainList = currentMediaDetailsForEdit?.creditsData?.map((d) => {
      return {
        label: d.name + (d?.email ? "(" + d.email + ")" : ""),
        value: d.id,
      };
    });
    const newMainList = constUserList?.map((d: UserModel) => {
      return {
        label: d.name + (d?.email ? "(" + d.email + ")" : ""),
        value: d.id,
      };
    });
    setMainListCredit(
      mainList && mainList?.length > 0 ? mainList : newMainList
    );

    const updatedUserContributorDataList =
      currentMediaDetailsForEdit?.userContributorData?.map((val: any) => {
        return {
          label: ` ${val?.name}  ${val?.email ? "(" + val?.email + ")" : ""} `,
          value: val?.id,
        };
      }) || [];
    setSelecteduserContributorData(
      (prev) =>
        deduplicateContributorOptions([
          ...(prev || []),
          ...(updatedUserContributorDataList as unknown as UserModel[]),
        ]) as UserModel[]
    );
  }, [currentMediaDetailsForEdit, constUserList, isApplied]);

  return (
    <>
      <div className="row gy-2">
        {mediaDetailConfig.contributors.enabled && (
          <>
            {hasPolicyValueGreaterThanOne("contributor", selectedProduct) && (
              <h6 className="mt-4 text-uppercase text-secondary">
                Contributors
              </h6>
            )}
            {contributorConfig.producer.enabled &&
              shouldShowField(
                selectedProduct?.contributor_policy?.producer
              ) && (
                <div className="col col-md-4 col-sm-12">
                  <CustomSelect
                    label="Producer"
                    value={processPermissionForSelect(formik.values.producer)}
                    handleChange={(newValue: any) => {
                      handlePermissionChange(
                        newValue.map((option: Option) => option.value),
                        "producer"
                      );
                      store.dispatch(
                        setUserList({ data: { list: [], count: 0 } })
                      );
                    }}
                    onInputChangeCallback={(params) => {
                      if (params.keyword.trim().length >= 3) {
                        store.dispatch(getListUser?.({ name: params.keyword }));
                      } else {
                        store.dispatch(
                          setUserList({ data: { list: [], count: 0 } })
                        );
                      }
                    }}
                    sortMode="fuzzy"
                    onMenuClose={clearUserList}
                    options={producers}
                    handleBlur={formik.handleBlur}
                    multiSelect={true}
                    touched={!!formik.touched.producer}
                    hideArrow={true}
                    disabled={!mediaEditableConfig.contributors}
                    updatedOptions={contributorsList}
                    loader={true}
                    showRequired={isFieldMandatoryStrict(
                      selectedProduct?.contributor_policy?.producer
                    )}
                    error={formik.errors.producer}
                    creditData={selecteduserContributorData}
                  />
                </div>
              )}
            {contributorConfig.anchor.enabled &&
              shouldShowField(selectedProduct?.contributor_policy?.anchor) && (
                <div className="col col-md-4 col-sm-12">
                  <CustomSelect
                    label="Anchor"
                    value={processPermissionForSelect(formik.values.anchor)}
                    handleChange={(newValue: any) => {
                      handlePermissionChange(
                        newValue.map((option: Option) => option.value),
                        "anchor"
                      );
                      store.dispatch(
                        setUserList({ data: { list: [], count: 0 } })
                      );
                    }}
                    onInputChangeCallback={(params) => {
                      if (params.keyword.trim().length >= 3) {
                        store.dispatch(getListUser?.({ name: params.keyword }));
                      } else {
                        store.dispatch(
                          setUserList({ data: { list: [], count: 0 } })
                        );
                      }
                    }}
                    sortMode="fuzzy"
                    onMenuClose={clearUserList}
                    options={anchors}
                    handleBlur={formik.handleBlur}
                    multiSelect={true}
                    touched={!!formik.touched.anchor}
                    disabled={!mediaEditableConfig.contributors}
                    hideArrow={true}
                    updatedOptions={contributorsList}
                    loader={true}
                    showRequired={isFieldMandatoryStrict(
                      selectedProduct?.contributor_policy?.anchor
                    )}
                    error={formik.errors.anchor}
                    creditData={selecteduserContributorData}
                  />
                </div>
              )}
            {contributorConfig.script_writer.enabled &&
              shouldShowField(
                selectedProduct?.contributor_policy?.script_writer
              ) && (
                <div className="col col-md-4 col-sm-12">
                  <CustomSelect
                    label="Script Writer"
                    value={processPermissionForSelect(
                      formik.values.script_writer
                    )}
                    handleChange={(newValue: any) => {
                      handlePermissionChange(
                        newValue.map((option: Option) => option.value),
                        "script_writer"
                      );
                      store.dispatch(
                        setUserList({ data: { list: [], count: 0 } })
                      );
                    }}
                    onInputChangeCallback={(params) => {
                      if (params.keyword.trim().length >= 3) {
                        store.dispatch(getListUser?.({ name: params.keyword }));
                      } else {
                        store.dispatch(
                          setUserList({ data: { list: [], count: 0 } })
                        );
                      }
                    }}
                    sortMode="fuzzy"
                    onMenuClose={clearUserList}
                    options={scriptWriters}
                    handleBlur={formik.handleBlur}
                    multiSelect={true}
                    touched={!!formik.touched.script_writer}
                    hideArrow={true}
                    disabled={!mediaEditableConfig.contributors}
                    updatedOptions={contributorsList}
                    loader={true}
                    showRequired={isFieldMandatoryStrict(
                      selectedProduct?.contributor_policy?.script_writer
                    )}
                    error={formik.errors.script_writer}
                    creditData={selecteduserContributorData}
                  />
                </div>
              )}
          </>
        )}
        {mediaDetailConfig.contributors.enabled && (
          <>
            {contributorConfig.video_editor.enabled &&
              shouldShowField(selectedProduct?.contributor_policy?.editor) && (
                <div className="col col-md-4 col-sm-12">
                  <CustomSelect
                    label="Editor"
                    value={processPermissionForSelect(formik.values.editor)}
                    disabled={!mediaEditableConfig.contributors}
                    handleChange={(newValue: any) => {
                      handlePermissionChange(
                        newValue.map((option: Option) => option.value),
                        "editor"
                      );
                      store.dispatch(
                        setUserList({ data: { list: [], count: 0 } })
                      );
                    }}
                    onInputChangeCallback={(params) => {
                      if (params.keyword.trim().length >= 3) {
                        store.dispatch(getListUser?.({ name: params.keyword }));
                      } else {
                        store.dispatch(
                          setUserList({ data: { list: [], count: 0 } })
                        );
                      }
                    }}
                    sortMode="fuzzy"
                    onMenuClose={clearUserList}
                    options={editors}
                    error={formik.errors.editor}
                    handleBlur={formik.handleBlur}
                    multiSelect={true}
                    touched={!!formik.touched.editor}
                    hideArrow={true}
                    updatedOptions={contributorsList}
                    loader={true}
                    showRequired={isFieldMandatoryStrict(
                      selectedProduct?.contributor_policy?.editor
                    )}
                    creditData={selecteduserContributorData}
                  />
                </div>
              )}
            {contributorConfig.cameraman.enabled &&
              shouldShowField(
                selectedProduct?.contributor_policy?.cameraman
              ) && (
                <div className="col col-md-4 col-sm-12">
                  <CustomSelect
                    label="Cameraman"
                    value={processPermissionForSelect(formik.values.cameraman)}
                    handleChange={(newValue: any) => {
                      handlePermissionChange(
                        newValue.map((option: Option) => option.value),
                        "cameraman"
                      );
                      store.dispatch(
                        setUserList({ data: { list: [], count: 0 } })
                      );
                    }}
                    onInputChangeCallback={(params) => {
                      if (params.keyword.trim().length >= 3) {
                        store.dispatch(getListUser?.({ name: params.keyword }));
                      } else {
                        store.dispatch(
                          setUserList({ data: { list: [], count: 0 } })
                        );
                      }
                    }}
                    sortMode="fuzzy"
                    onMenuClose={clearUserList}
                    options={cameramans}
                    handleBlur={formik.handleBlur}
                    multiSelect={true}
                    touched={!!formik.touched.cameraman}
                    hideArrow={true}
                    disabled={!mediaEditableConfig.contributors}
                    updatedOptions={contributorsList}
                    loader={true}
                    creditData={selecteduserContributorData}
                    showRequired={isFieldMandatoryStrict(
                      selectedProduct?.contributor_policy?.cameraman
                    )}
                    error={formik.errors.cameraman}
                  />
                </div>
              )}
          </>
        )}
        {mediaDetailConfig.contributors.enabled && (
          <>
            {contributorConfig.photographer.enabled &&
              shouldShowField(
                selectedProduct?.contributor_policy?.photographer
              ) && (
                <div className="col col-md-4 col-sm-12">
                  <CustomSelect
                    label="Photographer"
                    value={processPermissionForSelect(
                      formik.values.photographer
                    )}
                    disabled={!mediaEditableConfig.contributors}
                    handleChange={(newValue: any) => {
                      handlePermissionChange(
                        newValue.map((option: Option) => option.value),
                        "photographer"
                      );
                      store.dispatch(
                        setUserList({ data: { list: [], count: 0 } })
                      );
                    }}
                    onInputChangeCallback={(params) => {
                      if (params.keyword.trim().length >= 3) {
                        store.dispatch(getListUser?.({ name: params.keyword }));
                      } else {
                        store.dispatch(
                          setUserList({ data: { list: [], count: 0 } })
                        );
                      }
                    }}
                    sortMode="fuzzy"
                    onMenuClose={clearUserList}
                    options={editors}
                    error={formik.errors.photographer}
                    handleBlur={formik.handleBlur}
                    multiSelect={true}
                    touched={!!formik.touched.photographer}
                    hideArrow={true}
                    updatedOptions={contributorsList}
                    loader={true}
                    showRequired={isFieldMandatoryStrict(
                      selectedProduct?.contributor_policy?.photographer
                    )}
                    creditData={selecteduserContributorData}
                  />
                </div>
              )}
            {contributorConfig.researcher.enabled &&
              shouldShowField(
                selectedProduct?.contributor_policy?.researcher
              ) && (
                <div className="col col-md-4 col-sm-12">
                  <CustomSelect
                    label="Researcher"
                    value={processPermissionForSelect(formik.values.researcher)}
                    handleChange={(newValue: any) => {
                      handlePermissionChange(
                        newValue.map((option: Option) => option.value),
                        "researcher"
                      );
                      store.dispatch(
                        setUserList({ data: { list: [], count: 0 } })
                      );
                    }}
                    onInputChangeCallback={(params) => {
                      if (params.keyword.trim().length >= 3) {
                        store.dispatch(getListUser?.({ name: params.keyword }));
                      } else {
                        store.dispatch(
                          setUserList({ data: { list: [], count: 0 } })
                        );
                      }
                    }}
                    sortMode="fuzzy"
                    onMenuClose={clearUserList}
                    options={cameramans}
                    handleBlur={formik.handleBlur}
                    multiSelect={true}
                    touched={!!formik.touched.researcher}
                    hideArrow={true}
                    disabled={!mediaEditableConfig.contributors}
                    updatedOptions={contributorsList}
                    loader={true}
                    creditData={selecteduserContributorData}
                    showRequired={isFieldMandatoryStrict(
                      selectedProduct?.contributor_policy?.researcher
                    )}
                    error={formik.errors.researcher}
                  />
                </div>
              )}
            {contributorConfig.compiler.enabled &&
              shouldShowField(
                selectedProduct?.contributor_policy?.compiler
              ) && (
                <div className="col col-md-4 col-sm-12">
                  <CustomSelect
                    label="Compiler"
                    value={processPermissionForSelect(formik.values.compiler)}
                    handleChange={(newValue: any) => {
                      handlePermissionChange(
                        newValue.map((option: Option) => option.value),
                        "compiler"
                      );
                      store.dispatch(
                        setUserList({ data: { list: [], count: 0 } })
                      );
                    }}
                    onInputChangeCallback={(params) => {
                      if (params.keyword.trim().length >= 3) {
                        store.dispatch(getListUser?.({ name: params.keyword }));
                      } else {
                        store.dispatch(
                          setUserList({ data: { list: [], count: 0 } })
                        );
                      }
                    }}
                    sortMode="fuzzy"
                    onMenuClose={clearUserList}
                    options={cameramans}
                    handleBlur={formik.handleBlur}
                    multiSelect={true}
                    touched={!!formik.touched.compiler}
                    hideArrow={true}
                    disabled={!mediaEditableConfig.contributors}
                    updatedOptions={contributorsList}
                    loader={true}
                    creditData={selecteduserContributorData}
                    showRequired={isFieldMandatoryStrict(
                      selectedProduct?.contributor_policy?.compiler
                    )}
                    error={formik.errors.compiler}
                  />
                </div>
              )}
          </>
        )}
        {mediaDetailConfig.contributors.enabled && (
          <>
            {contributorConfig.curator.enabled &&
              shouldShowField(selectedProduct?.contributor_policy?.curator) && (
                <div className="col col-md-4 col-sm-12">
                  <CustomSelect
                    label="Curator"
                    value={processPermissionForSelect(formik.values.curator)}
                    disabled={!mediaEditableConfig.contributors}
                    handleChange={(newValue: any) => {
                      handlePermissionChange(
                        newValue.map((option: Option) => option.value),
                        "curator"
                      );
                      store.dispatch(
                        setUserList({ data: { list: [], count: 0 } })
                      );
                    }}
                    onInputChangeCallback={(params) => {
                      if (params.keyword.trim().length >= 3) {
                        store.dispatch(getListUser?.({ name: params.keyword }));
                      } else {
                        store.dispatch(
                          setUserList({ data: { list: [], count: 0 } })
                        );
                      }
                    }}
                    sortMode="fuzzy"
                    onMenuClose={clearUserList}
                    options={editors}
                    error={formik.errors.curator}
                    handleBlur={formik.handleBlur}
                    multiSelect={true}
                    touched={!!formik.touched.curator}
                    hideArrow={true}
                    updatedOptions={contributorsList}
                    loader={true}
                    showRequired={isFieldMandatoryStrict(
                      selectedProduct?.contributor_policy?.curator
                    )}
                    creditData={selecteduserContributorData}
                  />
                </div>
              )}
            {contributorConfig.reporter.enabled &&
              shouldShowField(
                selectedProduct?.contributor_policy?.reporter
              ) && (
                <div className="col col-md-4 col-sm-12">
                  <CustomSelect
                    label="Reporter"
                    value={processPermissionForSelect(formik.values.reporter)}
                    handleChange={(newValue: any) => {
                      handlePermissionChange(
                        newValue.map((option: Option) => option.value),
                        "reporter"
                      );
                      store.dispatch(
                        setUserList({ data: { list: [], count: 0 } })
                      );
                    }}
                    onInputChangeCallback={(params) => {
                      if (params.keyword.trim().length >= 3) {
                        store.dispatch(getListUser?.({ name: params.keyword }));
                      } else {
                        store.dispatch(
                          setUserList({ data: { list: [], count: 0 } })
                        );
                      }
                    }}
                    sortMode="fuzzy"
                    onMenuClose={clearUserList}
                    options={cameramans}
                    handleBlur={formik.handleBlur}
                    multiSelect={true}
                    touched={!!formik.touched.cameraman}
                    hideArrow={true}
                    disabled={!mediaEditableConfig.contributors}
                    updatedOptions={contributorsList}
                    loader={true}
                    creditData={selecteduserContributorData}
                    showRequired={isFieldMandatoryStrict(
                      selectedProduct?.contributor_policy?.reporter
                    )}
                    error={formik.errors.reporter}
                  />
                </div>
              )}
            {contributorConfig.author.enabled &&
              shouldShowField(selectedProduct?.contributor_policy?.author) && (
                <div className="col col-md-4 col-sm-12">
                  <CustomSelect
                    label="Author"
                    value={processPermissionForSelect(formik.values.author)}
                    handleChange={(newValue: any) => {
                      handlePermissionChange(
                        newValue.map((option: Option) => option.value),
                        "author"
                      );
                      store.dispatch(
                        setUserList({ data: { list: [], count: 0 } })
                      );
                    }}
                    onInputChangeCallback={(params) => {
                      if (params.keyword.trim().length >= 3) {
                        store.dispatch(getListUser?.({ name: params.keyword }));
                      } else {
                        store.dispatch(
                          setUserList({ data: { list: [], count: 0 } })
                        );
                      }
                    }}
                    sortMode="fuzzy"
                    onMenuClose={clearUserList}
                    options={cameramans}
                    handleBlur={formik.handleBlur}
                    multiSelect={true}
                    touched={!!formik.touched.author}
                    hideArrow={true}
                    disabled={!mediaEditableConfig.contributors}
                    updatedOptions={contributorsList}
                    loader={true}
                    creditData={selecteduserContributorData}
                    showRequired={isFieldMandatoryStrict(
                      selectedProduct?.contributor_policy?.author
                    )}
                    error={formik.errors.author}
                  />
                </div>
              )}
          </>
        )}
      </div>
      {mediaDetailConfig.creditors.enabled &&
        shouldShowField(selectedProduct?.credits_policy?.imagecredit) && (
          <div className="row gy-2">
            {hasPolicyValueGreaterThanOne("credits", selectedProduct) && (
              <h6 className="mt-4 text-uppercase text-secondary">Credits</h6>
            )}
            <div className="col-md-4 col-sm-12">
              <CustomSelect
                label="Image Credit"
                value={processPermissionForSelect(formik.values.imagecredit)}
                handleChange={(newValue: any) => {
                  handlePermissionChange(
                    newValue.map((option: Option) => option.value),
                    "imagecredit"
                  );
                  store.dispatch(getCreditList({ name: "", key: "image" }));
                }}
                onInputChangeCallback={(params) => {
                  if (params.keyword.trim().length >= 3) {
                    store.dispatch(
                      getCreditList({ name: params.keyword, key: "image" })
                    );
                  } else {
                    store.dispatch(
                      setImageCreditList({
                        data: { list: [] },
                        isLoading: false,
                        error: "",
                      })
                    );
                  }
                }}
                options={imageCreditList}
                handleBlur={formik.handleBlur}
                multiSelect={true}
                touched={!!formik.touched.imagecredit}
                sortMode="fuzzy"
                onMenuClose={() =>
                  store.dispatch(
                    setImageCreditList({
                      data: { list: [] },
                      isLoading: false,
                      error: "",
                    })
                  )
                }
                hideArrow={true}
                updatedOptions={imageCreditList}
                disabled={!mediaEditableConfig.creditors}
                creditData={mainListCredit}
                checkBox={false}
                loader={true}
                showRequired={isFieldMandatoryStrict(
                  selectedProduct?.credits_policy?.imagecredit
                )}
                error={formik.errors.imagecredit}
              />
            </div>
            {shouldShowField(selectedProduct?.credits_policy?.videocredit) && (
              <div className="col-md-4 col-sm-12">
                <CustomSelect
                  label="Video Credit"
                  value={processPermissionForSelect(formik.values.videocredit)}
                  handleChange={(newValue: any) => {
                    handlePermissionChange(
                      newValue.map((option: Option) => option.value),
                      "videocredit"
                    );
                    store.dispatch(getCreditList({ name: "", key: "video" }));
                  }}
                  onInputChangeCallback={(params) => {
                    if (params.keyword.trim().length >= 3) {
                      store.dispatch(
                        getCreditList({ name: params.keyword, key: "video" })
                      );
                    } else {
                      store.dispatch(
                        setVideoCreditList({
                          data: { list: [] },
                          isLoading: false,
                          error: "",
                        })
                      );
                    }
                  }}
                  options={videoCreditList}
                  handleBlur={formik.handleBlur}
                  disabled={!mediaEditableConfig.creditors}
                  multiSelect={true}
                  touched={!!formik.touched.videocredit}
                  sortMode="fuzzy"
                  onMenuClose={() =>
                    store.dispatch(
                      setVideoCreditList({
                        data: { list: [] },
                        isLoading: false,
                        error: "",
                      })
                    )
                  }
                  hideArrow={true}
                  updatedOptions={videoCreditList}
                  creditData={mainListCredit}
                  checkBox={false}
                  loader={true}
                  showRequired={isFieldMandatoryStrict(
                    selectedProduct?.credits_policy?.videocredit
                  )}
                  error={formik.errors.videocredit}
                />
              </div>
            )}
            {shouldShowField(selectedProduct?.credits_policy?.audiocredit) && (
              <div className="col-md-4 col-sm-12">
                <CustomSelect
                  label="Audio Credit"
                  value={processPermissionForSelect(formik.values.audiocredit)}
                  handleChange={(newValue: any) => {
                    handlePermissionChange(
                      newValue.map((option: Option) => option.value),
                      "audiocredit"
                    );
                    store.dispatch(getCreditList({ name: "", key: "audio" }));
                  }}
                  onInputChangeCallback={(params) => {
                    if (params.keyword.trim().length >= 3) {
                      store.dispatch(
                        getCreditList({ name: params.keyword, key: "audio" })
                      );
                    } else {
                      store.dispatch(
                        setAudioCreditList({
                          data: { list: [] },
                          isLoading: false,
                          error: "",
                        })
                      );
                    }
                  }}
                  options={audioCreditList}
                  handleBlur={formik.handleBlur}
                  multiSelect={true}
                  touched={!!formik.touched.audiocredit}
                  sortMode="fuzzy"
                  onMenuClose={() =>
                    store.dispatch(
                      setAudioCreditList({
                        data: { list: [] },
                        isLoading: false,
                        error: "",
                      })
                    )
                  }
                  hideArrow={true}
                  updatedOptions={audioCreditList}
                  creditData={mainListCredit}
                  checkBox={false}
                  loader={true}
                  disabled={!mediaEditableConfig.creditors}
                  showRequired={isFieldMandatoryStrict(
                    selectedProduct?.credits_policy?.audiocredit
                  )}
                  error={formik.errors.audiocredit}
                />
              </div>
            )}
            {!isContentType2 &&
              shouldShowField(selectedProduct?.agency_credit_policy) && (
                <div className="col-md-4 col-sm-12">
                  <CustomSelect
                    label="Agency Credits"
                    disabled={!mediaEditableConfig.agency_credits}
                    value={processPermissionForSelect(
                      formik?.values?.agency_credits || []
                    )}
                    handleChange={(newValue: any) => {
                      handlePermissionChange(
                        newValue.map((option: Option) => option.value),
                        "agency_credits"
                      );
                    }}
                    options={autoSelectAgencyCredit.map((item: any) => ({
                      label: formatEntityName(item),
                      value: item?.id,
                    }))}
                    handleBlur={formik.handleBlur}
                    onInputChangeCallback={(params) => {
                      if (params.keyword.trim().length >= 3) {
                        const result = getListAgencies?.({
                          name: params.keyword,
                          setInRedux: false,
                          status: STATUS_LIST.ACTIVE,
                        });
                        if (
                          result &&
                          typeof (result as any).then === "function"
                        ) {
                          (result as Promise<any>).then((res: any) =>
                            setAutoSelectAgencyCredit(res || [])
                          );
                        } else if (Array.isArray(result)) {
                          setAutoSelectAgencyCredit(result || []);
                        } else {
                          setAutoSelectAgencyCredit([]);
                        }
                      } else {
                        setAutoSelectAgencyCredit([]);
                      }
                    }}
                    sortMode="fuzzy"
                    onMenuClose={() => setAutoSelectAgencyCredit([])}
                    multiSelect={true}
                    hideArrow={true}
                    showRequired={isFieldMandatoryStrict(
                      selectedProduct?.agency_credit_policy
                    )}
                    error={formik.errors.agency_credits}
                    loader={true}
                    touched={!!formik.touched.agency_credits}
                    updatedOptions={autoSelectAgencyCredit.map((item: any) => ({
                      label: formatEntityName(item),
                      value: item?.id,
                    }))}
                    creditData={selectedAgency_credits}
                  />
                </div>
              )}
          </div>
        )}
      <div className="row gy-2">
        {hasPolicyValueGreaterThanOne("geo", selectedProduct) && (
          <h6 className="mt-4 text-uppercase text-secondary">
            Geo Restriction
          </h6>
        )}
        {shouldShowField(selectedProduct?.geo_policy?.gct) && (
          <div className="col col-md-4">
            <CustomSelect
              value={formik.values.gct}
              label="Mode"
              options={[
                { label: "Allowed Countries", value: 1 },
                { label: "Blocked Countries", value: 2 },
              ]}
              disabled={false}
              placeholder="Mode"
              handleChange={(newValue: any) => {
                formik.setFieldValue("gct", newValue.value);
              }}
              hideArrow={true}
              showRequired={isFieldMandatoryStrict(
                selectedProduct?.geo_policy?.gct
              )}
              error={formik.errors.gct}
              touched={!!formik.touched.gct}
            />
          </div>
        )}
        {shouldShowField(selectedProduct?.geo_policy?.gcl) && (
          <div className="col col-md-8">
            <CustomSelect
              label="Countries"
              options={COUNTRIES}
              disabled={false}
              placeholder="Countries"
              handleChange={(newValue: any) => {
                const newCountries = [
                  ...new Set(newValue.map((v: any) => v.value)),
                ];
                formik.setFieldValue("gcl", newCountries);
              }}
              value={formik.values.gcl}
              multiSelect={true}
              handleClear={() => formik.setFieldValue("gcl", [])}
              updatedOptions={COUNTRIES}
              hideArrow={true}
              showRequired={isFieldMandatoryStrict(
                selectedProduct?.geo_policy?.gcl
              )}
              error={formik.errors.gcl}
              touched={!!formik.touched.gcl}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default ContributorsAndCreditors;
